import { useState } from 'react';
import EmbeddedCardLayout from '../../../components/embedded/CardLayout';
import EmbeddedQrCodeCard from '../../../components/embedded/QrCodeCard';
import EmbeddedInstallInstructions from '../../../components/embedded/InstallInstructions';
import EmbeddedCaption from '../../../components/embedded/Caption';
import EmbeddedNeedHelpSection from '../../../components/embedded/NeedHelpSection';
import { installLinksMap } from '../../../data/installLinks';
import { type AgentStartPageProps } from '../../../chulak/ChulakSession';
import EmbeddedDetailsScreen from '../shared/DetailsScreen';
import EmbeddedMissingInstallScreen from '../shared/MissingInstallScreen';
import EmbeddedUnsupportedScreen from '../shared/UnsupportedScreen';
import { useAnotherApp, useEmbeddedCopy } from '../../../hooks/useEmbeddedCopy';
import { type ViewTable } from '../../types';

type View = 'qr' | 'details' | 'install';

/**
 * Scan (desktop and tablets): the QR code grows with its content, with
 * "No app?" and "What is shared?" in the help row.
 */
export default function EmbeddedDesktopAgentStartPage({
  tags,
  app,
  onBack,
}: AgentStartPageProps) {
  const { t, pick, issuance } = useEmbeddedCopy();
  const [view, setView] = useState<View>('qr');
  const anotherApp = useAnotherApp(onBack);
  const appName = app?.name ?? '';
  const links = installLinksMap[appName];

  if (tags.type === 'Redirect') {
    return <EmbeddedUnsupportedScreen app={app} onBack={onBack} />;
  }

  const views: ViewTable<View> = {
    qr: () => (
      <EmbeddedCardLayout
        secondary={anotherApp}
        help={
          <EmbeddedNeedHelpSection
            links={[
              {
                label: t('embedded.qr.noApp', { app: appName }),
                onClick: () => setView('install'),
              },
              {
                label: t(
                  pick('embedded.qr.whatShared', 'embedded.qr.whatAdded'),
                ),
                onClick: () => setView('details'),
              },
            ]}
          />
        }
      >
        <EmbeddedQrCodeCard
          data={tags.type === 'Qr' ? tags.qr.content : ''}
          logo={app?.logo}
          label={t('embedded.qr.label', { app: appName })}
        />
        <EmbeddedCaption
          text={t(
            issuance ? 'embedded.qr.captionIssuance' : 'embedded.qr.caption',
          )}
          bold={appName}
        />
      </EmbeddedCardLayout>
    ),
    details: () => (
      <EmbeddedDetailsScreen
        back={{
          label: t('embedded.details.back'),
          onClick: () => setView('qr'),
        }}
      />
    ),
    install: () =>
      links ? (
        <EmbeddedCardLayout
          minHeight={358}
          primary={{
            label: t('embedded.install.installed'),
            onClick: () => setView('qr'),
          }}
          secondary={anotherApp}
        >
          <EmbeddedInstallInstructions
            appName={appName}
            links={links}
            label={t('embedded.install.label', { app: appName })}
          />
          <EmbeddedCaption
            text={t('embedded.install.caption')}
            bold={appName}
          />
        </EmbeddedCardLayout>
      ) : (
        <EmbeddedMissingInstallScreen
          app={app}
          onInstalled={() => setView('qr')}
          onBack={onBack}
        />
      ),
  };

  return views[view]();
}
